import { useSyncExternalStore } from 'react'
import { graphqlClient } from '@/core/api/graphql-client'
import {
  type EarnedCard,
  pendingIds,
  selectEarnedCards,
} from '@/features/collection/earned-cards'
import { CollectionEarnedCardsDocument } from '@/generated/graphql'

/**
 * Carries a finished practice's earned cards to the nav, the only place that can draw them.
 *
 * Module state rather than a query or a context: it has to survive the route change out of the
 * practice, and it is not server state — the server cannot say which cards *this* practice earned,
 * only what is pending now, so the answer is a diff this holds across two fetches.
 */
let snapshot: ReadonlySet<string> | null = null
let held: EarnedCard[] | null = null
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((l) => {
    l()
  })
}

async function fetchPendingChanges() {
  const data = await graphqlClient.request(CollectionEarnedCardsDocument)
  return data.myCollection.pendingChanges
}

/**
 * What the wall was already holding before this practice. Taken on entry, because a card left
 * unrevealed from an earlier day is pending too and must not join the fan.
 */
export async function snapshotPendingCards(): Promise<void> {
  try {
    snapshot = pendingIds(await fetchPendingChanges())
  } catch {
    // A missed snapshot must not fan yesterday's cards, so stay null and let arming no-op.
    snapshot = null
  }
}

/** The server has confirmed the completion; hold whatever it added. */
export async function armEarnedCards(): Promise<void> {
  if (snapshot === null) return
  try {
    held = selectEarnedCards(await fetchPendingChanges(), snapshot)
    // One completion, one fan: the cards are now the snapshot for whatever follows.
    snapshot = null
    emit()
  } catch {
    held = null
  }
}

/** The fan has played, or the user left before it could. */
export function clearEarnedCards(): void {
  if (held === null) return
  held = null
  emit()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useHeldEarnedCards(): EarnedCard[] | null {
  return useSyncExternalStore(
    subscribe,
    () => held,
    () => null,
  )
}

/** Tests only — module state outlives a single test otherwise. */
export function resetEarnedCardsForTest(): void {
  snapshot = null
  held = null
  listeners.clear()
}
